import React, { useState, useEffect, useCallback, useRef } from "react";
import { Bell } from "lucide-react";
import api from "@/lib/api";

const DOT_COLORS = { info: "bg-blue-500", warning: "bg-yellow-500", error: "bg-red-500" };

function showDesktopNotifications(data, seenRef) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  const unreadNow = data.filter((n) => !n.read);
  if (seenRef.current !== null) {
    const fresh = unreadNow.filter((n) => !seenRef.current.has(n.id));
    fresh.slice(0, 3).forEach((n) => {
      try {
        new Notification(n.title || "SSVP Account Pro", { body: n.message || "", icon: "/logo.webp", tag: n.id });
      } catch (e) {
        console.debug("Desktop notification failed:", e);
      }
    });
  }
  seenRef.current = new Set(data.map((n) => n.id));
}

function NotificationPanel({ items, onMarkAll }) {
  return (
    <div className="absolute right-0 mt-2 w-80 bg-white border border-zinc-200 rounded-md shadow-lg z-50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
        <span className="font-heading font-semibold text-sm">Notifications</span>
        <button onClick={onMarkAll} className="text-xs text-orange-700 hover:underline">Mark all read</button>
      </div>
      <div className="max-h-96 overflow-auto">
        {items.length === 0 && <p className="px-4 py-6 text-sm text-zinc-400 text-center">No notifications</p>}
        {items.map((n) => (
          <div key={n.id} className={`px-4 py-3 border-b border-zinc-50 ${n.read ? "opacity-60" : "bg-orange-50/30"}`}>
            <div className="flex items-start gap-2">
              <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${DOT_COLORS[n.level] || "bg-zinc-400"}`} />
              <div>
                <p className="text-sm font-medium text-zinc-900">{n.title}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{n.message}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const seenRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
      showDesktopNotifications(data, seenRef);
    } catch (e) {
      console.warn("Failed to load notifications:", e);
    }
  }, []);

  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const unread = items.filter((i) => !i.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <div className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-md hover:bg-zinc-100 transition-colors"
      >
        <Bell className="h-5 w-5 text-zinc-600" strokeWidth={1.8} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-orange-700 text-white text-[10px] font-bold flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <NotificationPanel items={items} onMarkAll={markAll} />
        </>
      )}
    </div>
  );
}
